import React, { useState } from "react";
import { colors } from "../../styles/colors";
import { NoteEntry, NoteType, NoteLocation, FinanceDirection, genNoteId } from "./noteTypes";
import { LocationPicker, NoteMapView } from "./NoteMap";

interface MilestoneNotesProps {
  notes: NoteEntry[];
  onChange: (notes: NoteEntry[]) => void;
  isPremium: boolean;
  readOnly?: boolean;
  onReveal: (noteId: string) => Promise<string | null>;
  onUpgrade?: () => void;
}

const fmtDate = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/* ── icons ── */
const IconText = ({ c = colors.textTertiary }: { c?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 13h6M9 17h4" />
  </svg>
);
const IconLock = ({ c = colors.textTertiary }: { c?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const IconPin = ({ c = colors.textTertiary }: { c?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" />
  </svg>
);
const IconTrash = ({ c = colors.textTertiary }: { c?: string }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const IconFinance = ({ c = colors.textTertiary }: { c?: string }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const TYPE_META: Record<NoteType, { label: string; icon: React.FC<{ c?: string }> }> = {
  text: { label: "Normal", icon: IconText },
  secure: { label: "Secure note", icon: IconLock },
  location: { label: "Location", icon: IconPin },
  finance: { label: "Finance", icon: IconFinance },
};

const CURRENCIES = ["USD", "EUR", "GBP", "AUD", "CAD", "INR", "LKR", "SGD", "JPY", "AED"];

export const formatMoney = (amount?: number, currency?: string): string => {
  const cur = currency || "USD";
  const val = typeof amount === "number" ? amount : 0;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(val);
  } catch {
    return `${cur} ${val.toFixed(2)}`;
  }
};

const INCOME_COLOR = "#16a34a";
const EXPENSE_COLOR = "#ef4444";

const MilestoneNotes: React.FC<MilestoneNotesProps> = ({
  notes,
  onChange,
  isPremium,
  readOnly = false,
  onReveal,
  onUpgrade,
}) => {
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<Record<string, boolean>>({});

  const [activeTab, setActiveTab] = useState<NoteType>("text");
  const [draftText, setDraftText] = useState("");
  const [draftLocation, setDraftLocation] = useState<NoteLocation | null>(null);
  const [draftDirection, setDraftDirection] = useState<FinanceDirection>("expense");
  const [draftAmount, setDraftAmount] = useState("");
  const [draftCurrency, setDraftCurrency] = useState("USD");
  const [draftDate, setDraftDate] = useState("");

  const sorted = [...notes].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));

  const remove = (id: string) => onChange(notes.filter((n) => n.id !== id));

  const isPremiumType = (type: NoteType) => type === "secure" || type === "location" || type === "finance";

  const selectTab = (type: NoteType) => {
    if (isPremiumType(type) && !isPremium) {
      onUpgrade?.();
      return;
    }
    setActiveTab(type);
  };

  const resetComposer = () => {
    setDraftText("");
    setDraftLocation(null);
    setDraftAmount("");
    setDraftDirection("expense");
    setDraftDate("");
  };

  const canAdd =
    activeTab === "location"
      ? !!draftLocation
      : activeTab === "finance"
      ? Number(draftAmount) > 0
      : draftText.trim().length > 0;

  const handleAdd = () => {
    if (isPremiumType(activeTab) && !isPremium) {
      onUpgrade?.();
      return;
    }
    if (!canAdd) return;
    const base: NoteEntry = { id: genNoteId(), type: activeTab, createdAt: new Date().toISOString() };
    if (activeTab === "text" || activeTab === "secure") base.content = draftText.trim();
    if (activeTab === "location" && draftLocation) base.location = draftLocation;
    if (activeTab === "finance") {
      base.amount = Number(draftAmount);
      base.currency = draftCurrency;
      base.direction = draftDirection;
      if (draftText.trim()) base.content = draftText.trim();
      if (draftDate) base.date = draftDate;
    }
    onChange([base, ...notes]);
    resetComposer();
  };

  const handleReveal = async (note: NoteEntry) => {
    // Newly-added (unsaved) secure notes still hold their plaintext in memory.
    if (note.content !== undefined) {
      setRevealed((r) => ({ ...r, [note.id]: note.content || "" }));
      return;
    }
    setRevealing((r) => ({ ...r, [note.id]: true }));
    try {
      const content = await onReveal(note.id);
      if (content !== null) setRevealed((r) => ({ ...r, [note.id]: content ?? "" }));
    } finally {
      setRevealing((r) => ({ ...r, [note.id]: false }));
    }
  };

  /* ── Tab selector ── */
  const TabButton = ({ type }: { type: NoteType }) => {
    const meta = TYPE_META[type];
    const Icon = meta.icon;
    const active = activeTab === type;
    const locked = isPremiumType(type) && !isPremium;
    return (
      <button
        type="button"
        onClick={() => selectTab(type)}
        title={locked ? `${meta.label} — Premium` : meta.label}
        aria-label={meta.label}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "9px 18px",
          borderRadius: 8,
          border: `1px solid ${active ? colors.brandPrimary : colors.borderLight}`,
          background: active ? "rgba(0,136,255,0.08)" : "#fff",
          color: active ? colors.brandPrimary : colors.textSecondary,
          cursor: "pointer",
        }}
      >
        <Icon c={active ? colors.brandPrimary : colors.textSecondary} />
      </button>
    );
  };

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${colors.borderLight}`,
    borderRadius: 12,
    background: "#fff",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };
  const headerStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 };
  const typeChip = (type: NoteType) => {
    const meta = TYPE_META[type];
    const Icon = meta.icon;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: colors.textTertiary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
        <Icon /> {meta.label}
      </span>
    );
  };
  const textareaStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    resize: "vertical",
    border: `1px solid ${colors.borderLight}`,
    borderRadius: 8,
    padding: "8px 10px",
    fontSize: 13,
    lineHeight: 1.6,
    color: colors.textPrimary,
    outline: "none",
    fontFamily: "inherit",
  };
  const smallBtn = (color: string): React.CSSProperties => ({
    border: "none",
    background: "transparent",
    color,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    padding: "2px 4px",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {!readOnly && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, border: `1px solid ${colors.brandPrimary}`, borderRadius: 12, padding: 12, background: "#fff" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            <TabButton type="text" />
            <TabButton type="secure" />
            <TabButton type="location" />
            <TabButton type="finance" />
          </div>

          {activeTab === "text" && (
            <textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              placeholder="Write your note…"
              rows={3}
              style={textareaStyle}
            />
          )}

          {activeTab === "secure" && (
            <>
              <textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="Enter secret content — encrypted on save"
                rows={3}
                style={textareaStyle}
              />
              <span style={{ fontSize: 11, color: colors.textTertiary, display: "flex", alignItems: "center", gap: 5 }}>
                <IconLock c={colors.textTertiary} /> Encrypted at rest. Hidden until unlocked.
              </span>
            </>
          )}

          {activeTab === "location" && (
            <LocationPicker value={draftLocation} onChange={setDraftLocation} />
          )}

          {activeTab === "finance" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", gap: 8 }}>
                {(["expense", "income"] as FinanceDirection[]).map((d) => {
                  const on = draftDirection === d;
                  const col = d === "income" ? INCOME_COLOR : EXPENSE_COLOR;
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDraftDirection(d)}
                      style={{
                        flex: 1,
                        padding: "8px 0",
                        borderRadius: 8,
                        border: `1px solid ${on ? col : colors.borderLight}`,
                        background: on ? `${col}14` : "#fff",
                        color: on ? col : colors.textSecondary,
                        fontSize: 13,
                        fontWeight: 700,
                        textTransform: "capitalize",
                        cursor: "pointer",
                      }}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <select
                  value={draftCurrency}
                  onChange={(e) => setDraftCurrency(e.target.value)}
                  style={{ border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", background: "#fff", color: colors.textPrimary }}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draftAmount}
                  onChange={(e) => setDraftAmount(e.target.value)}
                  placeholder="Amount"
                  style={{ flex: 1, boxSizing: "border-box", border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", color: colors.textPrimary }}
                />
              </div>
              <input
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                placeholder="Description (optional)"
                style={{ boxSizing: "border-box", border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", color: colors.textPrimary }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: colors.textTertiary }}>Date (optional)</span>
                <input
                  type="date"
                  value={draftDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDraftDate(e.target.value)}
                  style={{ boxSizing: "border-box", border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "8px 10px", fontSize: 13, outline: "none", fontFamily: "inherit", color: colors.textPrimary, background: "#fff" }}
                />
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!canAdd}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: canAdd ? colors.brandPrimary : colors.borderLight,
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: canAdd ? "pointer" : "not-allowed",
              }}
            >
              Add note
            </button>
          </div>
        </div>
      )}

      {sorted.length === 0 && (
        <div style={{ fontSize: 13, color: colors.textTertiary, padding: "8px 2px" }}>
          {readOnly ? "No notes." : "No notes added yet."}
        </div>
      )}

      {sorted.map((note) => (
        <div key={note.id} style={cardStyle}>
          <div style={headerStyle}>
            {typeChip(note.type)}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, color: colors.textTertiary }}>{fmtDate(note.createdAt)}</span>
              {!readOnly && (
                <button type="button" onClick={() => remove(note.id)} title="Delete note" style={{ ...smallBtn(colors.textTertiary), display: "flex", alignItems: "center" }}>
                  <IconTrash />
                </button>
              )}
            </div>
          </div>

          {/* ── Text (read-only) ── */}
          {note.type === "text" && (
            <div style={{ fontSize: 13, color: colors.textSecondary, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
              {note.content || "—"}
            </div>
          )}

          {/* ── Secure (read-only, unlock to view) ── */}
          {note.type === "secure" && (
            revealed[note.id] !== undefined ? (
              <>
                <div style={{ fontSize: 13, color: colors.textPrimary, whiteSpace: "pre-wrap", lineHeight: 1.6, background: "rgba(0,0,0,0.02)", border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "8px 10px" }}>
                  {revealed[note.id] || "(empty)"}
                </div>
                <button type="button" onClick={() => setRevealed((r) => { const n = { ...r }; delete n[note.id]; return n; })} style={{ ...smallBtn(colors.textTertiary), alignSelf: "flex-start" }}>
                  Hide
                </button>
              </>
            ) : (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "rgba(0,0,0,0.02)", border: `1px solid ${colors.borderLight}`, borderRadius: 8, padding: "10px 12px" }}>
                <span style={{ letterSpacing: 3, color: colors.textTertiary, fontSize: 14 }}>••••••••</span>
                <button type="button" onClick={() => handleReveal(note)} disabled={!!revealing[note.id]} style={{ ...smallBtn(colors.brandPrimary), display: "flex", alignItems: "center", gap: 5 }}>
                  <IconLock c={colors.brandPrimary} />
                  {revealing[note.id] ? "Unlocking…" : "Unlock"}
                </button>
              </div>
            )
          )}

          {/* ── Finance (read-only) ── */}
          {note.type === "finance" && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: note.direction === "income" ? INCOME_COLOR : EXPENSE_COLOR, textTransform: "capitalize" }}>
                  {note.direction || "expense"}
                  {note.date && (
                    <span style={{ fontWeight: 500, color: colors.textTertiary, textTransform: "none" }}>
                      {"  \u00b7  "}
                      {new Date(`${note.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  )}
                </span>
                {note.content && (
                  <span style={{ fontSize: 12, color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{note.content}</span>
                )}
              </div>
              <span style={{ fontSize: 15, fontWeight: 800, color: note.direction === "income" ? INCOME_COLOR : EXPENSE_COLOR, whiteSpace: "nowrap" }}>
                {note.direction === "income" ? "+" : "\u2212"}{formatMoney(note.amount, note.currency)}
              </span>
            </div>
          )}

          {/* ── Location (read-only) ── */}
          {note.type === "location" && (
            note.location ? (
              <>
                <NoteMapView location={note.location} />
                <span style={{ fontSize: 12, color: colors.textSecondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {note.location.label || `${note.location.lat.toFixed(4)}, ${note.location.lng.toFixed(4)}`}
                </span>
              </>
            ) : (
              <span style={{ fontSize: 12, color: colors.textTertiary }}>No location set.</span>
            )
          )}
        </div>
      ))}
    </div>
  );
};

export default MilestoneNotes;
